import { format, startOfWeek } from "date-fns";

/**
 * Returns a date key in 'yyyy-MM-dd' format for grouping/charting.
 * Accepts a Date object or a date string (ISO or parseable by Date).
 */
export function getDateKeyForGrouping(dateInput: string | Date): string {
    const date =
        typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    return format(date, "yyyy-MM-dd");
}

/**
 * Formats a date for display (e.g., 'dd/MM/yyyy').
 * Accepts a Date object or a date string (ISO or parseable by Date).
 */
export function formatDateForDisplay(
    dateInput: string | Date,
    displayFormat: string = "dd/MM/yyyy",
): string {
    const date =
        typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    return format(date, displayFormat);
}

/**
 * Returns a month key in 'yyyy-MM' format for grouping/charting by month.
 * Accepts a Date object or a date string (ISO or parseable by Date).
 */
export function getMonthKeyForGrouping(dateInput: string | Date): string {
    const date =
        typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    return format(date, "yyyy-MM");
}

/**
 * Returns the start of the week (Monday) for a given date as 'yyyy-MM-dd'.
 */
export function getWeekStartKey(dateInput: string | Date): string {
    const date =
        typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    return format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-MM-dd");
}

/**
 * Returns the week range as a display string, e.g. 'Apr 1–Apr 7, 2024'.
 */
export function formatWeekRange(weekStart: string): string {
    const start = new Date(weekStart);
    return `${format(start, "MM/dd")}`;
}

/**
 * Determines the grouping granularity based on the date range.
 * Returns 'day', 'week', or 'month'.
 */
export function getGroupingGranularity(
    from: Date,
    to: Date,
): "day" | "week" | "month" {
    const diffMs = to.getTime() - from.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > 90) return "month";
    if (diffDays > 30) return "week";
    return "day";
}
